import { Handle, Position } from 'reactflow';
import {
  Badge,
  Card,
  Flex,
  Separator,
  Text,
  IconButton,
} from '@radix-ui/themes';
import { Pencil2Icon } from '@radix-ui/react-icons';

import {
  SignalType,
  SlotDirection,
  WorkflowNodeSlot,
  CreateWorkflowNodeSlotInput,
} from '@/types';
import ConditionContainer from '@/components/ConditionContainer';
import { useCreateWorkflowNodeSlotMutation } from '@/graphql/mutations/CreateWorkflowNodeSlot.generated';

import CategorySection from './CategorySection';
import SlotSection from './Slot';

interface NodeData {
  inputs?: WorkflowNodeSlot[];
  outputs?: WorkflowNodeSlot[];
  controlFlows?: WorkflowNodeSlot[];
}

interface NodeContainerProps<T extends NodeData> {
  title: string;
  icon?: React.ReactNode;
  signalType?: SignalType;
  nodeId?: string;
  data?: T;
  children: React.ReactNode;
  style?: React.CSSProperties;
  onEdit?: () => void;
  isEditing?: boolean;
}

const NodeContainer = <T extends NodeData>({
  title,
  icon,
  data,
  signalType,
  nodeId,
  children,
  onEdit,
  isEditing,
  ...props
}: NodeContainerProps<T>) => {
  const [createWorkflowNodeSlot] = useCreateWorkflowNodeSlotMutation();

  const handleSlotSave = async (slotData: CreateWorkflowNodeSlotInput) => {
    if (!nodeId) return;

    try {
      await createWorkflowNodeSlot({
        variables: {
          input: {
            ...slotData,
            nodeId,
          },
        },
        // Refetch the node data after creating the slot
        refetchQueries: ['GetWorkflowNode'],
      });
    } catch (error) {
      console.error('Failed to create workflow node slot:', error);
    }
  };

  return (
    <Flex
      direction="column"
      width="250px"
      className="workflow-node-container"
      style={{
        border: '1px solid var(--gray-6)',
        borderRadius: '0.5rem',
        backgroundColor: 'var(--gray-1)',
        overflow: 'hidden',
        ...props.style,
      }}
      {...props}
    >
      <Flex
        p="2"
        style={{ borderBottom: '1px solid var(--gray-6)' }}
        align="center"
        gap="2"
        justify="between"
      >
        <Flex align="center" gap="2">
          {icon && icon}
          <Text size="2" weight="bold">
            {title}
          </Text>
        </Flex>
        {onEdit && !isEditing && (
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            onClick={onEdit}
            style={{
              cursor: 'pointer',
              marginRight: '-4px',
            }}
          >
            <Pencil2Icon width={12} height={12} />
          </IconButton>
        )}
      </Flex>
      {children}
      {data?.inputs && data.inputs.length > 0 && (
        <>
          <Separator size="4" />
          <CategorySection title="Inputs" count={data.inputs.length}>
            {data.inputs.map((input) => (
              <Card key={input.key} variant="ghost" size="1">
                <ConditionContainer>
                  <Badge variant="soft">{input.key}</Badge>
                  <Badge color="gray" variant="surface">
                    <Text size="1" style={{ textTransform: 'initial' }}>
                      {input.dataType}
                    </Text>
                  </Badge>
                </ConditionContainer>
                <Handle
                  id={input.key}
                  type="target"
                  position={Position.Left}
                  style={{ background: 'var(--gray-9)' }}
                />
              </Card>
            ))}
          </CategorySection>
        </>
      )}
      {data?.outputs && data.outputs.length > 0 && (
        <>
          <Separator size="4" />
          <CategorySection title="Outputs" count={data.outputs.length}>
            {data.outputs.map((output) => (
              <Card key={output.key} variant="ghost" size="1">
                <ConditionContainer>
                  <Badge variant="soft">{output.key}</Badge>
                  <Badge color="gray" variant="surface">
                    <Text size="1" style={{ textTransform: 'initial' }}>
                      {output.dataType}
                    </Text>
                  </Badge>
                </ConditionContainer>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={output.key}
                  style={{ background: 'var(--gray-9)' }}
                />
              </Card>
            ))}
            {signalType && nodeId && (
              <SlotSection
                direction={SlotDirection.Output}
                signalType={signalType}
                onSave={handleSlotSave}
                label="Add Output"
                existingSlots={data.outputs}
              />
            )}
          </CategorySection>
        </>
      )}
      {signalType &&
        nodeId &&
        (!data?.outputs || data.outputs.length === 0) && (
          <>
            <Separator size="4" />
            <CategorySection title="Outputs" count={0}>
              <SlotSection
                direction={SlotDirection.Output}
                signalType={signalType}
                onSave={handleSlotSave}
                label="Add Output"
                existingSlots={data?.outputs}
              />
            </CategorySection>
          </>
        )}
      {data?.controlFlows && data.controlFlows.length > 0 && (
        <>
          <Separator size="4" />
          <CategorySection
            title="Control Flow"
            count={data.controlFlows.length}
          >
            {data.controlFlows.map((trigger) => (
              <Card key={trigger.key} variant="ghost" size="1">
                <ConditionContainer>
                  <Badge variant="soft">{trigger.key}</Badge>
                </ConditionContainer>
                <Handle
                  type={
                    trigger.direction === SlotDirection.Input
                      ? 'target'
                      : 'source'
                  }
                  position={
                    trigger.direction === SlotDirection.Input
                      ? Position.Left
                      : Position.Right
                  }
                  id={trigger.key}
                  style={{ background: 'var(--gray-9)' }}
                />
              </Card>
            ))}
          </CategorySection>
        </>
      )}
    </Flex>
  );
};

export default NodeContainer;
